import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import commonFunctions from '../../../utils/commonFunctions';
import ConfirmModal from '../../../components/Modals/ConfirmModal';
import RedirectModal from './RedirectModal';
import ChevronLeftIcon from '../../../assets/icons/alert-plus/chevron-left.svg';
import ClearAlertIcon from '../../../assets/icons/alert-plus/bell-off.svg';
import AlertRequestsTable from '../AlertRequestsTable/AlertRequestsTable'; 
import useInfiniteScroll from '../../../components/common/useInfiniteScroll';
import useWebSocket from '../../../components/common/useWebSocket';
import API from '../../../utils/API';
import Toast from '../../../components/Toast/Toast';
import './ClassroomDetailsPage.css';

const ClassroomDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const websocket = useWebSocket();
  const { closeModal } = commonFunctions;

  const { alertTemplate, siteId, alertInProgressId } = location.state || {};

  const handleBackClick = () => {
    navigate('/alert-plus/manage');
  };

  const [teacherStatuses, setTeacherStatuses] = useState([]);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [dataLoaded, setDataLoaded] = useState(false);
  const [sortByCol, setSortByCol] = useState('name');
  const [sortByAsc, setSortByAsc] = useState(true);
  const [startIndex, setStartIndex] = useState(0);

  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const tableWrapperRef = useRef();
  const tableRef = useRef();
  const redirectModalButtonRef = useRef();

  const statusMapping = {
    'none': 'No response',
    'secure': 'Secured',
  };

  const dataMap = React.useMemo(() => {
    return { data: {}, ids: [], selectedIds: {}, filteredIds: [] };
  }, []);

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndex,
    startIndex,
    pageSize: 100, 
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  // WebSocket event listener
  useEffect(() => {
    if (!websocket) return;

    const handleWebSocketMessage = (message) => {
      const data = JSON.parse(message.data);
      if (data.messageId === 5001) {
        checkAlertInProgress();
        loadTable(dataMap, false);
      }
    };

    websocket.addEventListener('message', handleWebSocketMessage);

    return () => {
      websocket.removeEventListener('message', handleWebSocketMessage);
    };
  }, [websocket]);

  /**
   * Check if the alert in progress is still active, if not redirect to manage page
   */
  const checkAlertInProgress = () => {
    API.getSites()
      .then((response) => {
        const sites = response.data;
        // Check if the alert in progress is still active
        const siteAlertInProgress = sites.some((site) => site.alert_in_progress_id === alertInProgressId);
        // If the alert in progress is no longer active, redirect to the manage page
        if (!siteAlertInProgress) {
          if (redirectModalButtonRef.current) {
            redirectModalButtonRef.current.click(); // Trigger the click event to open the modal
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching sites:', error);
      });
  };

  const loadTable = (map, resetSelection = true) => {
    // Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    // Fetch teacher status data
    API.getTeacherStatus(alertInProgressId)
      .then((response) => {
        const teacherStatusData = response.data;
        console.log('getting teacher status data for', alertInProgressId, ':', teacherStatusData)
        teacherStatusData.forEach((status) => {
          map.data[status.id] = {
            id: status.id,
            name: status.name,
            phoneNumber: status.phone,
            status: status.status,
            checked: false,
          };
        });
        map.filteredIds = Object.keys(map.data);
        map.ids = Object.keys(map.data);
        setTeacherStatuses(teacherStatusData);
        setDataCount(map.ids.length);
        setTotalDataCount(map.ids.length);
        setDataLoaded(true);
      })
      .catch((error) => {
        console.error('Error fetching teacher status:', error);
      });
  };

  useEffect(() => {
    loadTable(dataMap);
  }, [alertInProgressId]);

  useEffect(() => {
    if (dataLoaded) {
      sortMap(dataMap);
      setData(generateDataSlice(dataMap));
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, teacherStatuses]);

  const handleClearAlert = async () => {
    console.log('clearing alert?', siteId)
    try {
      await API.clearAlert(siteId);
      setSuccess(<FormattedMessage id="ALERT_CLEAR_SUCCESS" />);
      closeModal('confirmModal');
      navigate('/alert-plus/manage', { state: { success: 'ALERT_CLEAR_SUCCESS' } }); // Pass string identifier
    } catch (error) {
      console.error('Error clearing alert:', error);
      setError(<FormattedMessage id="ALERT_CLEAR_FAIL" />)
    }
  };

  const noResponseCount = data.filter(item => item.status === 'none').length;
  const securedCount = data.filter(item => item.status === 'secure').length;

  // Initialize table columns
  const tableColumnArray = [
    {
      Header: 'Teacher',
      accessor: 'name',
    },
    {
      Header: 'Phone Number',
      accessor: 'phoneNumber',
    },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ value }) => (
        <span className={`status-cell ${value.replace(/\s+/g, '-').toLowerCase()}`}>
          {statusMapping[value]}
        </span>
      ),
    },
  ];

  const tableColumns = React.useMemo(() => tableColumnArray, []);

  // Package props into an object
  const tableProps = {
    headerTitle: "USER_STATUS",
    columns: tableColumns,
    data,
    dataCount,
    totalDataCount,
    dataLoaded,
    generateDataSlice,
    sortMap,
    setSortBy,
    sortByCol,
    sortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    startIndex,
  };

  return (
    <div className="classroom-details-page">
      <div className="back-button-container">
        <div className="back-button" onClick={handleBackClick}>
          <img src={ChevronLeftIcon} className="back-icon" alt="back-icon" />
          <FormattedMessage id="BACK" defaultMessage="Back" />
        </div>
      </div>
      <div className="alert-info">
        <div className="alert-info-left alert-header" style={{ color: alertTemplate.background_color }}>
          {alertTemplate.icon && <img src={alertTemplate.icon} className="alert-icon" alt={`${alertTemplate.name} icon`} />}
          <span className="alert-type"><FormattedMessage id={alertTemplate.name.toUpperCase()} /></span>
          &nbsp;<FormattedMessage id="ALERT_IN_PROGRESS" />
        </div>
        <div className="alert-buttons">
          <button id="clearAlertBtn" className="clear-alert-button" data-bs-toggle="modal" data-bs-target="#confirmModal">
            <img src={ClearAlertIcon} alt="Clear Alert" className="clear-alert-icon" />
            <FormattedMessage id="CLEAR_ALERT" />
          </button>
        </div>
      </div>
      <div className="status-counters">
        <div className="status-box no-response">
          <FormattedMessage id="NO_RESPONSE" defaultMessage="No response" />: {noResponseCount}
        </div>
        <div className="status-box secured">
          <FormattedMessage id="SECURED" defaultMessage="Secured" />: {securedCount}
        </div>
      </div>
      <div className="alert-content">
        <AlertRequestsTable {...tableProps} />
      </div>
      <ConfirmModal 
        handleDelete={handleClearAlert}
        dataType="Alert"
        checkedItems={[siteId]}
        closeModal={closeModal}
      />
      <RedirectModal closeModal={closeModal} />
      <button
        type="button"
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#redirectModal"
        ref={redirectModalButtonRef}
      >
        Open Redirect Modal
      </button>
      <Toast
        success={success}
        error={error}
        setSuccess={setSuccess}
        setError={setError}
      />
    </div>
  );
};

export default ClassroomDetailsPage;
